import { defaultDateDef, defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { FourWeekAvgCellRenderer } from '@components/AnalyticsReporting/Forecast/FourWeekAvgCellRenderer'
import { ForecastOverrideCellRenderer } from '@components/StoreMonitoring/StoreDetailsPage/TabView/Forecast/ForecastOverrideCellRenderer'
import {
  ForecastDisplayDate,
  VarianceCellRenderer,
} from '@components/StoreMonitoring/StoreDetailsPage/TabView/Forecast/Forecasts'
import { addCommasToNumber, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import React from 'react'
import moment from 'moment'
import dayjs from 'dayjs'

export const getColumnDefs = ({
  deleteOverride,
  overrideVolume,
  visiblePopoverId,
  setVisiblePopoverId,
  hasOverridePermission = false,
  storeTimeZone,
  isManifoldChild,
  varianceThreshold,
}) => {
  return [
    {
      cellRenderer: 'agGroupCellRenderer',
      field: 'date',
      valueFormatter: () => '',
      headerName: '',
      maxWidth: 50,
    },
    {
      field: 'date',
      headerName: 'Date',
      ...defaultDateDef('date'),
      cellRenderer: ({ data }) => <ForecastDisplayDate type={data.type} date={data.date} isHourlies={false} />,
      valueGetter: ({ data }) => dayjs(data.date).startOf('day'),
      sort: 'asc',
    },
    {
      field: 'date',
      headerName: 'Weekday',
      cellRenderer: ({ data }) => <ForecastDisplayDate type={data.type} date={data.date} isHourlies={false} isDay />,
      filterValueGetter: ({ data }) => {
        return formatters.DAY(data.date)
      },
    },
    {
      field: 'inventory',
      headerName: 'Beginning Inventory',
      ...defaultNumberDef('inventory', 0),
      cellRenderer: ({ data }) => (
        <Horizontal style={{ minHeight: 30 }} verticalCenter>
          {data.type === 'today' && (
            <Texto className='mr-1' weight={600} appearance='primary' align='right' style={{ flex: 2 }}>
              Current Volume:
            </Texto>
          )}
          {data.type !== 'past' && (
            <Texto style={{ flex: 1 }} align='right'>
              {addCommasToNumber(data.inventory, 0)}
            </Texto>
          )}
        </Horizontal>
      ),
      type: 'rightAligned',
    },

    {
      field: 'sales',
      headerName: 'Sales',
      ...defaultNumberDef('sales', 0),
      cellRenderer: ({ data }) => (
        <ForecastOverrideCellRenderer
          column='sales'
          value={data.sales}
          data={data}
          deleteOverride={deleteOverride}
          overrideVolume={overrideVolume}
          visiblePopoverId={visiblePopoverId}
          setVisiblePopoverId={setVisiblePopoverId}
          hasOverridePermission={hasOverridePermission}
          isManifoldChild={isManifoldChild}
        />
      ),
      type: 'rightAligned',
      cellClassRules: {
        'overridden-cell': ({ data }) => !!data.sales_override,
      },
      valueGetter: ({ data }) => data.sales || -Infinity,
    },
    {
      headerName: 'Forecast',
      field: 'forecast',
      ...defaultNumberDef('forecast', 0),
      cellRenderer: ({ data }) => (
        <ForecastOverrideCellRenderer
          column='forecast'
          value={data.forecast}
          data={data}
          deleteOverride={deleteOverride}
          overrideVolume={overrideVolume}
          visiblePopoverId={visiblePopoverId}
          setVisiblePopoverId={setVisiblePopoverId}
          hasOverridePermission={hasOverridePermission}
          storeTimeZone={storeTimeZone}
          isManifoldChild={isManifoldChild}
        />
      ),
      cellClassRules: {
        'overridden-cell': ({ data }) => !!data.forecast_override,
      },
      valueGetter: ({ data }) => data.forecast || -Infinity,
    },
    {
      headerName: 'Variance',
      field: 'variance',
      cellRenderer: ({ data }) => {
        if (!data.variance) return null

        const isToday = data.type === 'today'
        return (
          <VarianceCellRenderer
            forecast={isToday ? data.intraday_forecast : data.forecast}
            variance={data.variance}
            threshold={varianceThreshold}
          />
        )
      },
      ...defaultNumberDef('variance', 0),
      valueGetter: ({ data }) => data.variance || -Infinity,
    },
    {
      headerName: '4-Week Avg Sales',
      field: 'sales_avg',
      cellRenderer: ({ data }) => <FourWeekAvgCellRenderer data={data} />,
      ...defaultNumberDef('sales_avg', 0),
      valueGetter: ({ data }) => data.sales_avg || -Infinity,
    },
    {
      field: 'deliveries',
      headerName: 'Projected Deliveries',
      ...defaultNumberDef('deliveries', 0),
      cellRenderer: ({ data }) => (
        <Horizontal style={{ minHeight: 30 }} verticalCenter>
          {data.type === 'today' && (
            <Texto weight={600} appearance='primary' align='right' style={{ flex: 2 }}>
              Remaining:
            </Texto>
          )}
          {data.type !== 'past' && (
            <Texto appearance='secondary' weight={600} style={{ flex: 1 }} align='right'>
              {addCommasToNumber(data.deliveries, 0)}
            </Texto>
          )}
        </Horizontal>
      ),
      type: 'rightAligned',
    },
  ]
}

export const hourliesColumnDefs = () => {
  return [
    {
      field: 'date',
      headerName: 'Hour',
      ...defaultDateDef('date'),
      cellRenderer: ({ data }) => <ForecastDisplayDate type={data.type} date={data.date} isHourlies />,
      valueGetter: ({ data }) => dayjs(data.date),
      sort: 'asc',
      flex: 1,
    },
    {
      field: 'inventory',
      headerName: 'Beginning Inventory',
      ...defaultNumberDef('inventory', 0),
      cellRenderer: ({ data }) => (
        <Horizontal style={{ minHeight: 30 }} verticalCenter>
          {data.type === 'today' && (
            <Texto weight={600} appearance='primary' align='right' style={{ flex: 2 }}>
              Current Volume:
            </Texto>
          )}
          {data.type !== 'past' && (
            <Texto style={{ flex: 1 }} align='right'>
              {addCommasToNumber(data.inventory, 0)}
            </Texto>
          )}
        </Horizontal>
      ),
      type: 'rightAligned',
      flex: 0.5,
    },

    {
      field: 'sales',
      headerName: 'Sales',
      ...defaultNumberDef('sales', 0),
      cellRenderer: ({ data }) => (
        <Texto
          align='right'
          appearance={data?.type === 'past' ? 'primary' : 'secondary'}
          weight={600}
          style={{ marginRight: 43 }}
        >
          {addCommasToNumber(data.sales, 0)}
        </Texto>
      ),
      type: 'rightAligned',
      cellClassRules: {
        'overridden-cell': ({ data }) => !!data.override,
      },
      flex: 0.5,
    },

    {
      field: 'deliveries',
      headerName: 'Projected Deliveries',
      ...defaultNumberDef('deliveries', 0),
      cellRenderer: ({ data }) => (
        <Horizontal style={{ minHeight: 30 }} verticalCenter>
          {data.type === 'today' && (
            <Texto weight={600} appearance='primary' align='right' style={{ flex: 2 }}>
              Remaining:
            </Texto>
          )}
          {data.type !== 'past' && (
            <Texto appearance='secondary' weight={600} style={{ flex: 1 }} align='right'>
              {addCommasToNumber(data.deliveries, 0)}
            </Texto>
          )}
        </Horizontal>
      ),
      type: 'rightAligned',
      flex: 2,
    },
  ]
}
